import React, { ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

import CustomTextfield from '../form/CustomTextfield';
import {
  gray10,
  gray40,
  gray60,
  gray80,
  gray100,
  orange10,
  orange20,
  orange50,
  primaryButtonBackgroundColor,
  red20,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd, regularMd, regularSm, regularXl } from '../../../constants/location';
import {
  SUB_PATTERN_NAME_MAX_LENGTH,
  SUB_PATTERN_DESCRIPTION_MAX_LENGTH,
} from '../../../constants/enum';
import { subPatternLabel, subPatternJsonLabel } from '../../../constants/subPattern';
import SubPatternJsonModal from './SubPatternJsonModal';
import {
  buildSubPatternJson,
  stringifySubPatternJson,
  type SubPatternJsonType,
} from '../../../utils/pattern/subPatternJson';
import { fixedValuePlaceholder } from '../../../constants/itemValidation';
import type { ItemOverrideType, SubPatternType } from '../../../types/subPattern';
import type { ItemListType } from '../../../types/common';
import { validateSubPattern } from '../../../utils/pattern/subPatternValidation';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import { ProxyLoginContext } from '../../../contexts/ProxyLoginContext';
import type { SystemStyleObject, Theme } from '@mui/system';
import { PROXY_BANNER_HEIGHT, PROXY_DIALOG_GAP } from '../../../constants/labels';

/**
 * 子パターンの作成・編集モーダル（#2967）。
 *
 * 上書きできるのは取得ルールと固定値のみで、項目の増減や型の変更はできない。
 * 出力される項目の構成を親パターンと一致させ、結果一覧・CSV出力・マスタ照合を
 * 子パターンの有無にかかわらず同じ扱いにするため。
 *
 * 桁数などのレイアウト固有の条件は取得ルールの文章として書く。
 * 文字数制限（minLength 等）は結果画面のバリデーション表示と共用のため、
 * 子パターンごとに変えると同じ列でも行によってエラーの基準が変わってしまう。
 */

/** 上書き対象の候補1件。親パターンの項目から組み立てて渡す */
export type SubPatternOverrideCandidate = {
  itemId: string; // 親項目は "2"、明細の列は "2-1"
  itemName: string; // 画面に出す項目名。明細の列は「親項目名 > 子項目名」
  parentRule: string; // 親パターンの取得ルール（変更前の値として見せる）
  parentFixedValue: string; // 親パターンの固定値（変更前の値として見せる。#3314）
  itemType: string; // データ型（string / number / date など）。固定値欄の入力例の出し分けに使う
  parentIsOcrTarget: boolean; // 親パターンのAI読取ON/OFF。トグルの初期値に使う
  // AI読取のON/OFFを切り替えられるか。明細（array）の親は false（列ごとに設定する）
  canToggleOcrTarget: boolean;
  // 固定値を設定できるか。明細（array）の親は false
  // （行数が可変で固定値を入れる対象が定まらないため。項目編集モーダルと同じ扱い）
  canSetFixedValue: boolean;
};

type Props = {
  open: boolean;
  /** 編集対象。新規作成時は null */
  variant: SubPatternType | null;
  /** 上書きできる項目の一覧（親パターンの item_list から組み立てる） */
  candidates: SubPatternOverrideCandidate[];
  /** 同一パターンの既存の子パターン。名称の重複判定に使う */
  existingSubPatterns: SubPatternType[];
  /**
   * 親パターンの項目リスト（#3372）。
   * JSON入力画面で参照用に親パターンの設定JSONを出すために渡す
   */
  itemList?: ItemListType[];
  /** AI提案から開いた場合の初期値。項目IDをキーにした取得ルール */
  initialOverrides?: Record<string, string>;
  /** AI提案から開いた場合の初期値。項目IDをキーにした、この帳票での項目名（#2967） */
  initialItemNames?: Record<string, string>;
  /** AI提案の取得中か。true の間は入力を止めてローディングを見せる */
  suggesting?: boolean;
  /** AIに取得ルールを提案させる。未指定ならボタンを出さない（編集時など） */
  onResuggest?: () => void;
  /**
   * 値を直した項目があるか（#2967）。
   * 提案は直した項目だけを対象にするため、1件も無ければ実行させない
   */
  hasChangedItems?: boolean;
  /**
   * 値を直した項目の名前（#2967）。
   * 「どの項目に対して提案するのか」を示すために出す
   */
  changedItemNames?: string[];
  /** すでに提案を受け取ったか。ボタンの文言を「提案し直す」に変える */
  hasSuggested?: boolean;
  /**
   * 保存されていない編集があるか（#2967）。
   * AIは**保存済みの値**を見て提案するため、未保存だと直した内容が反映されない。
   * true の間は提案を実行させず、保存を促す
   */
  hasUnsavedEdits?: boolean;
  /**
   * 帳票を見ながら書けるよう、右寄せの縦長パネルにするか（#2967）。
   * 結果画面（左に帳票が出る）から開くときだけ true。
   * パターン一覧から開くときは背後に帳票が無いため、通常の中央ダイアログにする
   */
  alignRight?: boolean;
  /**
   * JSONでのコピー・編集を使えるか（#3372）。運営スタッフのみ true。
   * 項目IDを直接扱うため、誤入力で「上書きしたつもりが効いていない」状態を作れる。
   * 親パターンの項目リストJSON（#3039）と同じく運営スタッフ限定にする
   */
  canUseJson?: boolean;
  /**
   * 完了メッセージの表示（#3372）。
   * 「JSONをコピーしました」のような成功通知を、呼び出し側の snackbar で出す。
   * モーダル内のエラー枠（赤）は失敗の表示なので、成功をそこに出すと誤解させる
   */
  onShowMessage?: (message: string) => void;
  submitting: boolean;
  onClose: () => void;
  onSave: (input: {
    subPatternName: string;
    description: string;
    itemOverrides: ItemOverrideType[];
  }) => void;
};

const styles = {
  /** 標準の配置。パターン一覧から開くときはこちらだけを使う */
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '32px',
      width: '640px',
      maxWidth: '90vw',
      maxHeight: 'calc(100vh - 32px)',
      // ヘッダーと操作ボタンを固定し、中身だけスクロールさせる
      display: 'flex',
      flexDirection: 'column',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  /**
   * 結果画面から開いたときだけ足す差分（#2967）。帳票を見ながらルールを書けるようにする。
   *
   * - **右寄せ**: 結果画面は左5:右7の分割で、左に帳票が出る。右側（入力欄）の上に
   *   重ねることで、帳票を隠さずに作業できる
   * - **backdrop を薄く**: 帳票が透けて見える。ただし**消しはしない**。
   *   消すと背後の入力欄を触れてしまい、ルールを書いている最中に値が変わる
   * - **縦長**: 項目数が多いパターンでも、上書き対象を一度に見渡せるようにする
   */
  dialogAlignRight: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
    '& .MuiDialog-container': {
      justifyContent: 'flex-end',
    },
    '& .MuiPaper-root': {
      // 右側（入力欄）領域はビューポートの約7/12。その7割強を覆う幅にする
      width: '42vw',
      minWidth: '560px',
      height: 'calc(100vh - 32px)',
      '@media screen and (max-width: 960px)': {
        width: '100%',
        minWidth: 0,
      },
    },
  },
  /** 冒頭の説明。この機能が何をするものかを最初に伝える */
  introBox: {
    marginBottom: '16px',
  },
  /** AI提案の導線。値を直してから押してもらう */
  /**
   * AI提案の導線。**値を直してから押す**ことが前提の機能なので、
   * その一文を読み飛ばされないようオレンジで囲って目立たせる
   */
  suggestBox: {
    backgroundColor: orange10,
    border: `1px solid ${orange20}`,
    // 結果画面の要注意サマリー（ReviewSummaryPanel）と揃える
    borderRadius: '8px',
    marginBottom: '24px',
    padding: '12px 16px',
  },
  /** 「まず値を直してから押す」の一文。ここが読まれないと提案の精度が落ちる */
  suggestNotice: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: '6px',
    marginBottom: '4px',
  },
  suggestNoticeIcon: {
    color: orange50,
    fontSize: '18px',
    flexShrink: 0,
  },
  suggestButton: {
    height: '36px',
    marginTop: '12px',
    // 全幅にすると横に間延びして押しにくいため、内容に合わせた幅にする
    minWidth: '180px',
    paddingX: '20px',
    width: 'auto',
    // 共通の disabled 色（薄いオレンジ）は押せないことが伝わりにくいため、
    // グレーに落として文字と枠の両方を薄くする
    '&:disabled': {
      backgroundColor: gray10,
      border: `1px solid ${gray40}`,
      color: gray60,
      // 中身の Typography が自前の色を持つため、子要素まで上書きする
      '& .MuiTypography-root': {
        color: gray60,
      },
    },
  },
  /** ヘッダーと操作ボタンを固定し、中身だけスクロールさせる */
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    flexShrink: 0,
  },
  sectionLabel: {
    ...regularMd,
    color: gray100,
    marginBottom: '8px',
  },
  helperText: {
    ...regularSm,
    color: gray80,
    marginBottom: '8px',
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    // 同じ「囲って知らせる」系の表示なので、他の枠と半径を揃える
    borderRadius: '8px',
    color: red50,
    fontSize: '14px',
    marginBottom: '16px',
    padding: '12px 16px',
  },
  overrideList: {
    border: '1px solid' + gray40,
    borderRadius: '4px',
    // 縦長パネルではモーダル全体がスクロールするため、ここでは高さを制限しない。
    // 制限すると入れ子のスクロール領域ができ、どちらを動かせばよいか分かりにくくなる
  },
  overrideRow: {
    borderBottom: '1px solid' + gray40,
    padding: '12px 16px',
    '&:last-of-type': { borderBottom: 'none' },
  },
  /** 提案の対象になる項目名。太字で「これが対象」と分かるようにする */
  changedItemsText: {
    color: gray100,
    fontSize: '13px',
    fontWeight: 700,
    lineHeight: 1.8,
    marginTop: '4px',
  },
  /** 未保存の編集があるときの警告。提案は保存済みの値を見るため */
  unsavedAlert: {
    ...regularSm,
    color: red50,
    marginTop: '8px',
  },
  /**
   * 上書き設定の入れ物（#2967）。3つの設定は見出しの太字で区別し、
   * 間隔をあけて並べる。左の余白でチェックボックスと項目名にぶら下げ、
   * その項目に属する設定であることを示す
   */
  overrideSettings: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '12px',
    marginLeft: '26px',
  },
  /** AI読取のON/OFF。項目編集モーダルの同名トグルと同じ体裁にする */
  ocrTargetRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  ocrTargetLabel: {
    fontSize: '14px',
    fontWeight: 700,
    color: gray80,
  },
  ocrTargetHelper: {
    fontSize: '12px',
    color: gray80,
  },
  ocrTargetStateText: {
    fontSize: '12px',
    color: gray80,
    minWidth: '28px',
  },
  /** 通常（親パターン）の取得ルール。参考情報なので小さく弱く見せる */
  parentRuleText: {
    color: gray80,
    fontSize: '11px',
    lineHeight: 1.6,
    marginTop: '6px',
    whiteSpace: 'pre-wrap',
  },
  overrideRowChecked: {
    backgroundColor: gray10,
  },
  actions: {
    display: 'flex',
    gap: '16px',
    marginTop: '24px',
    flexShrink: 0,
  },
  actionButton: {
    flex: 1,
    height: '44px',
  },
  /** ヘッダー右側。JSONの操作と閉じるボタンを並べる（#3372） */
  headerRight: {
    alignItems: 'center',
    display: 'flex',
    gap: '16px',
  },
  /**
   * JSONのコピー・編集（#3372）。運営スタッフのみ。
   * 親パターンの項目リスト（ItemListManager）と同じ見た目にそろえる
   */
  jsonActions: {
    alignItems: 'center',
    display: 'flex',
    gap: '12px',
  },
  jsonAction: {
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    gap: '4px',
  },
  jsonActionIcon: {
    color: orange50,
    fontSize: '16px',
  },
  jsonActionText: {
    color: orange50,
    fontSize: '14px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  /** コピーと編集の間の細い区切り。親パターンと同じ色・寸法 */
  jsonActionDivider: {
    backgroundColor: '#CAD5E2',
    height: '16px',
    width: '1px',
  },
};

const SubPatternEditModal = (props: Props): ReactElement => {
  const {
    open,
    variant,
    candidates,
    existingSubPatterns,
    itemList = [],
    initialOverrides,
    initialItemNames,
    suggesting = false,
    onResuggest,
    hasChangedItems = true,
    changedItemNames = [],
    hasSuggested = false,
    hasUnsavedEdits = false,
    alignRight = false,
    canUseJson = false,
    onShowMessage,
    submitting,
    onClose,
    onSave,
  } = props;

  // 項目数が多いと全高まで伸びるダイアログのため、代理モードではバナー分を差し引く（#3310）
  const proxyDialogSx = useProxyDialogSx({ fullHeight: true });
  const { isProxyLogin } = useContext(ProxyLoginContext);
  // 右寄せ表示は maxHeight ではなく height を固定しているので、
  // フックの maxHeight だけでは縮まない。height 側も同じ計算で上書きする（#3310）
  const proxyAlignRightHeightSx: SystemStyleObject<Theme> = isProxyLogin
    ? {
        '& > .MuiDialog-container > .MuiPaper-root.MuiPaper-root': {
          height: `calc(100vh - ${PROXY_BANNER_HEIGHT + PROXY_DIALOG_GAP * 2}px)`,
        },
      }
    : {};

  const [subPatternName, setSubPatternName] = useState('');
  const [description, setDescription] = useState('');
  // 上書き対象として選んだ項目ID
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  // 項目IDごとの上書き内容
  const [rules, setRules] = useState<Record<string, string>>({});
  // AIに見せる項目名の上書き（#2967）。項目IDごとに保持する
  const [itemNames, setItemNames] = useState<Record<string, string>>({});
  // AI読取のON/OFFの上書き（#2967）。キーが無い項目は親の設定をそのまま使う
  const [ocrTargets, setOcrTargets] = useState<Record<string, boolean>>({});
  // 固定値の上書き（#3314）。項目IDごとに保持する
  const [fixedValues, setFixedValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  // JSON入力モーダルを開いているか（#3372）
  const [jsonModalOpen, setJsonModalOpen] = useState(false);

  /**
   * 編集対象（または提案内容）で入力を初期化する。
   *
   * **開くたびには初期化しない。** 呼び出し側が提案結果をキャッシュしており、
   * 一度閉じて開き直したときにユーザーが手で直した内容が残るようにするため。
   * 初期化するのは「閉じた状態から開いた瞬間」と「新しい提案が届いたとき」だけ。
   */
  const resetInputs = useCallback(() => {
    setError(null);
    // 編集時は保存済みの名称。新規作成時は空にする
    // （AIの名称提案は誤りが多かったため行わない）
    setSubPatternName(variant?.sub_pattern_name ?? '');
    setDescription(variant?.description ?? '');

    const nextRules: Record<string, string> = {};
    const nextItemNames: Record<string, string> = {};
    const nextOcrTargets: Record<string, boolean> = {};
    const nextFixedValues: Record<string, string> = {};
    const nextSelected = new Set<string>();

    // 編集時は保存済みの上書きを反映する
    for (const override of variant?.item_overrides ?? []) {
      nextRules[override.itemId] = override.extractionRule;
      nextItemNames[override.itemId] = override.itemName ?? '';
      nextFixedValues[override.itemId] = override.fixedValue ?? '';
      if (override.isOcrTarget !== undefined) {
        nextOcrTargets[override.itemId] = override.isOcrTarget;
      }
      nextSelected.add(override.itemId);
    }
    // AI提案の内容を反映する。提案の対象は「まだ上書きしていない項目」だけなので、
    // 保存済みの上書き（上のループで入れた分）とキーが衝突せず、手で書いたルールは残る
    if (initialOverrides) {
      for (const [itemId, rule] of Object.entries(initialOverrides)) {
        nextRules[itemId] = rule;
        nextSelected.add(itemId);
      }
    }
    // 帳票に印字されている見出しの提案も初期値にする（#2967）
    if (initialItemNames) {
      for (const [itemId, name] of Object.entries(initialItemNames)) {
        nextItemNames[itemId] = name;
      }
    }

    setRules(nextRules);
    setItemNames(nextItemNames);
    setOcrTargets(nextOcrTargets);
    setFixedValues(nextFixedValues);
    setSelectedItemIds(nextSelected);
  }, [variant, initialOverrides, initialItemNames]);

  /**
   * 初期化するのは次の2つのときだけ。
   *
   * 1. **編集対象が変わったとき**（別の子パターンを開いた・新規作成に切り替わった）
   * 2. **新しい提案が届いたとき**（AI提案は非同期で届く／「AIで再提案」を押した）
   *
   * **単に開き直しただけでは初期化しない。** 呼び出し側が提案をキャッシュしており、
   * 閉じて開き直してもユーザーが手で直した内容が残るようにするため。
   * ここで毎回初期化すると、キャッシュした意味が無くなる。
   */
  const initializedForRef = useRef<{
    subPatternId: string | null;
    overrides: Record<string, string> | undefined;
  } | null>(null);

  useEffect(() => {
    if (!open) return;

    const current = { subPatternId: variant?.subPatternId ?? null, overrides: initialOverrides };
    const previous = initializedForRef.current;
    const isSameTarget =
      previous !== null &&
      previous.subPatternId === current.subPatternId &&
      previous.overrides === current.overrides;
    if (isSameTarget) return;

    initializedForRef.current = current;
    resetInputs();
  }, [open, variant, initialOverrides, resetInputs]);

  const handleClose = (): void => {
    if (submitting) return;
    onClose();
  };

  const toggleItem = (itemId: string): void => {
    setSelectedItemIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  /**
   * その項目がこの子パターンでAI読取の対象か。
   * トグルを持たない項目（明細の親）は常に対象として扱う
   */
  const isOcrOn = (candidate: SubPatternOverrideCandidate): boolean =>
    !candidate.canToggleOcrTarget || (ocrTargets[candidate.itemId] ?? candidate.parentIsOcrTarget);

  /**
   * その項目のAI読取ON/OFFを上書きとして保存するか（#3314）。
   *
   * **親と値が違うときは必ず保存する。** 以前は「違うときだけ」保存していたが、
   * それだと子でONにした項目が親をONに戻した時点で上書きごと消え、
   * その後また親をOFFにすると読み取られない状態になっていた。
   *
   * 親と同じ値のときは、**他に上書きする内容がある場合だけ**一緒に保存する。
   * 常に保存すると、チェックを付けただけで何も入力していない項目まで
   * countActiveOverrides が「上書き1件」と数え、「N項目を上書き」の表示や
   * 「上書きを1つ以上」のバリデーションが実態とずれるため。
   */
  const resolveOcrTargetOverride = (
    candidate: SubPatternOverrideCandidate,
  ): boolean | undefined => {
    if (!candidate.canToggleOcrTarget) return undefined;

    const value = ocrTargets[candidate.itemId] ?? candidate.parentIsOcrTarget;
    if (value !== candidate.parentIsOcrTarget) return value;

    const hasOtherOverride =
      (rules[candidate.itemId] ?? '').trim() !== '' ||
      (itemNames[candidate.itemId] ?? '').trim() !== '' ||
      (candidate.canSetFixedValue ? (fixedValues[candidate.itemId] ?? '').trim() : '') !== '';
    return hasOtherOverride ? value : undefined;
  };

  const buildOverrides = (): ItemOverrideType[] =>
    candidates
      .filter(candidate => selectedItemIds.has(candidate.itemId))
      .map(candidate => ({
        itemId: candidate.itemId,
        extractionRule: rules[candidate.itemId] ?? '',
        itemName: itemNames[candidate.itemId] ?? '',
        // 固定値の上書き（#3314）。明細（array）の親は行数が可変で対象が定まらないため持たせない
        fixedValue: candidate.canSetFixedValue ? (fixedValues[candidate.itemId] ?? '') : '',
        isOcrTarget: resolveOcrTargetOverride(candidate),
      }));

  const handleSave = (): void => {
    const itemOverrides = buildOverrides();
    const validationError = validateSubPattern({
      subPatternName,
      description,
      itemOverrides,
      existingSubPatterns,
      editingSubPatternId: variant?.subPatternId,
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    onSave({
      subPatternName: subPatternName.trim(),
      description: description.trim(),
      itemOverrides,
    });
  };

  /**
   * 現在の入力内容をJSONにする（#3372）。
   * 保存済みの値ではなく編集中の内容を見るため、コピーと初期表示の両方でこれを使う
   */
  const buildCurrentJson = (): SubPatternJsonType =>
    buildSubPatternJson({
      subPatternName,
      description,
      // 有効/無効を切り替えるUIが無いため、既存の値を引き継ぐ（新規は有効）
      isActive: variant?.is_active !== false,
      itemOverrides: buildOverrides(),
    });

  const handleJsonCopy = async (): Promise<void> => {
    const json = buildCurrentJson();
    if (json.item_overrides.length === 0 && json.sub_pattern_name === '') {
      onShowMessage?.(subPatternJsonLabel.nothingToCopy);
      return;
    }
    try {
      await navigator.clipboard.writeText(stringifySubPatternJson(json));
      // 成功は snackbar（青）で出す。親パターンの項目リストJSONと同じ扱い
      onShowMessage?.(subPatternJsonLabel.copied);
    } catch {
      setError(subPatternJsonLabel.copyFailed);
    }
  };

  /**
   * JSONの内容をフォームへ反映する（#3372）。
   *
   * ここでは保存しない。反映後に画面で確認し、「保存」を押して初めて Firestore へ書く。
   * 上書きは全置換なので、JSONに無い項目のチェックは外れる
   */
  const handleJsonApply = (items: SubPatternJsonType[]): void => {
    const item = items[0];
    if (!item) return;

    setSubPatternName(item.sub_pattern_name);
    setDescription(item.description);

    const nextSelected = new Set<string>();
    const nextRules: Record<string, string> = {};
    const nextItemNames: Record<string, string> = {};
    const nextFixedValues: Record<string, string> = {};
    const nextOcrTargets: Record<string, boolean> = {};

    for (const override of item.item_overrides) {
      nextSelected.add(override.item_id);
      if (override.extraction_rule !== undefined) {
        nextRules[override.item_id] = override.extraction_rule;
      }
      if (override.item_name !== undefined) {
        nextItemNames[override.item_id] = override.item_name;
      }
      if (override.fixed_value !== undefined) {
        nextFixedValues[override.item_id] = override.fixed_value;
      }
      if (override.is_ocr_target !== undefined) {
        nextOcrTargets[override.item_id] = override.is_ocr_target;
      }
    }

    setSelectedItemIds(nextSelected);
    setRules(nextRules);
    setItemNames(nextItemNames);
    setFixedValues(nextFixedValues);
    setOcrTargets(nextOcrTargets);
    setError(null);
    setJsonModalOpen(false);
  };

  const isBusy = submitting || suggesting;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={
        alignRight
          ? [styles.dialog, styles.dialogAlignRight, proxyAlignRightHeightSx, proxyDialogSx]
          : [styles.dialog, proxyDialogSx]
      }
    >
      <Box sx={styles.header}>
        <Typography sx={regularXl}>
          {variant ? '子パターンを編集' : '子パターンとして保存'}
        </Typography>
        <Box sx={styles.headerRight}>
          {/*
            JSONでのコピー・編集（#3372）。運営スタッフのみ。
            親パターンの項目リスト（ItemListManager）と同じ見た目・同じ並びにそろえる
          */}
          {canUseJson && (
            <Box sx={styles.jsonActions}>
              <Box
                sx={styles.jsonAction}
                onClick={(): void => {
                  if (!isBusy) void handleJsonCopy();
                }}
              >
                <ContentCopyOutlinedIcon sx={styles.jsonActionIcon} />
                <Typography sx={styles.jsonActionText}>{subPatternJsonLabel.copy}</Typography>
              </Box>
              <Box sx={styles.jsonActionDivider} />
              <Box
                sx={styles.jsonAction}
                onClick={(): void => {
                  if (!isBusy) setJsonModalOpen(true);
                }}
              >
                <FileUploadOutlinedIcon sx={styles.jsonActionIcon} />
                <Typography sx={styles.jsonActionText}>{subPatternJsonLabel.edit}</Typography>
              </Box>
            </Box>
          )}
          <IconButton onClick={handleClose} disabled={isBusy} sx={{ padding: 0 }}>
            <CloseIcon sx={{ height: '32px', width: '32px' }} />
          </IconButton>
        </Box>
      </Box>

      {error && <Box sx={styles.errorBox}>{error}</Box>}

      <Box sx={styles.scrollArea}>
        {/* この機能が何をするものかを最初に説明する（#2967） */}
        {!variant && (
          <Box sx={styles.introBox}>
            <Typography sx={{ ...boldMd, color: gray100 }}>{subPatternLabel.introLead}</Typography>
            <Typography sx={{ ...regularSm, color: gray80, marginTop: '4px' }}>
              {subPatternLabel.introSub}
            </Typography>
          </Box>
        )}

        {/*
          AI提案は開いた時点では実行せず、ボタンで明示的に実行する（#2967）。
          AIは「ユーザーが直した正しい値」と帳票を見比べてルールを作るため、
          値を直す前に提案させると精度が落ちる。
        */}
        {onResuggest && (
          <Box sx={styles.suggestBox}>
            <Box sx={styles.suggestNotice}>
              <InfoOutlinedIcon sx={styles.suggestNoticeIcon} />
              <Typography sx={{ ...boldMd, color: orange50 }}>
                {subPatternLabel.suggestNotice}
              </Typography>
            </Box>
            <Typography sx={{ ...regularSm, color: gray80 }}>
              {subPatternLabel.suggestHelper}
            </Typography>
            {/*
              どの項目に対して提案するのかを明示する（#2967）。
              対象が分からないまま押すと、期待した項目が提案されず戸惑うため
            */}
            {changedItemNames.length > 0 && (
              <Typography sx={styles.changedItemsText}>
                {subPatternLabel.changedItemsPrefix}
                {changedItemNames.join('、')}
              </Typography>
            )}
            {/*
              未保存だと提案に反映されないため、保存を促して実行させない。
              AIが見るのは保存済みの result_data であり、画面上の編集中の値ではない
            */}
            {hasUnsavedEdits && (
              <Typography sx={styles.unsavedAlert}>{subPatternLabel.unsavedAlert}</Typography>
            )}
            <Button
              onClick={onResuggest}
              disabled={isBusy || hasUnsavedEdits || !hasChangedItems}
              sx={[secondaryButtonBackgroundColor, styles.suggestButton]}
            >
              {suggesting ? (
                <>
                  <CircularProgress size={16} sx={{ color: orange50, marginRight: '8px' }} />
                  <Typography sx={boldMd}>提案中...</Typography>
                </>
              ) : (
                <Typography sx={boldMd}>
                  {hasSuggested ? subPatternLabel.resuggest : subPatternLabel.suggest}
                </Typography>
              )}
            </Button>
          </Box>
        )}

        <CustomTextfield
          labelText={subPatternLabel.name}
          requiredText="必須"
          value={subPatternName}
          onChange={(e): void => setSubPatternName(e.target.value)}
          disabled={isBusy}
          inputWidth="100%"
          fontSizeIs14px
          inputProps={{ maxLength: SUB_PATTERN_NAME_MAX_LENGTH }}
          placeholder="例: A社フォーマット"
        />

        {/*
          仕分け判定に使う説明（#2967）。AIはこの文章と帳票を見比べて
          どの子パターンかを選ぶため、空欄だと一度も選ばれない
        */}
        <Box sx={{ marginTop: '16px' }}>
          <CustomTextfield
            labelText={subPatternLabel.description}
            requiredText="必須"
            description={subPatternLabel.descriptionHelper}
            value={description}
            onChange={(e): void => setDescription(e.target.value)}
            disabled={isBusy}
            multiline
            minRows={2}
            inputWidth="100%"
            fontSizeIs14px
            formMb="0"
            inputProps={{ maxLength: SUB_PATTERN_DESCRIPTION_MAX_LENGTH }}
            placeholder={subPatternLabel.descriptionPlaceholder}
          />
        </Box>

        <Box sx={{ marginTop: '24px' }}>
          <Typography sx={styles.sectionLabel}>{subPatternLabel.overrideSection}</Typography>
          <Typography sx={styles.helperText}>{subPatternLabel.overrideHelper}</Typography>

          <Box sx={styles.overrideList}>
            {candidates.length === 0 ? (
              <Box sx={styles.overrideRow}>
                <Typography sx={{ ...regularSm, color: gray80 }}>
                  上書きできる項目がありません。
                </Typography>
              </Box>
            ) : (
              candidates.map(candidate => {
                const checked = selectedItemIds.has(candidate.itemId);
                return (
                  <Box
                    key={candidate.itemId}
                    sx={{
                      ...styles.overrideRow,
                      ...(checked ? styles.overrideRowChecked : {}),
                    }}
                  >
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
                      <Checkbox
                        checked={checked}
                        onChange={(): void => toggleItem(candidate.itemId)}
                        disabled={isBusy}
                        size="small"
                        sx={{ padding: 0 }}
                      />
                      <Typography sx={{ ...regularSm, color: gray100, fontWeight: 700 }}>
                        {candidate.itemName}
                      </Typography>
                    </Box>

                    {checked && (
                      <Box sx={styles.overrideSettings}>
                        {/*
                          AI読取のON/OFF（#2967 / #2968）。
                          初期値は親パターンの設定で、変えたときだけ上書きになる。
                          OFFにするとプロンプトから外れ手入力欄になるが、
                          結果には空の項目として残るため出力される列は変わらない。
                          体裁は項目編集モーダルの「AI読取する」に合わせる
                        */}
                        {candidate.canToggleOcrTarget && (
                          <Box>
                            <Box sx={styles.ocrTargetRow}>
                              <Typography sx={styles.ocrTargetLabel}>
                                {subPatternLabel.ocrTargetToggle}
                              </Typography>
                              <Switch
                                size="small"
                                checked={
                                  ocrTargets[candidate.itemId] ?? candidate.parentIsOcrTarget
                                }
                                onChange={(e): void =>
                                  setOcrTargets(prev => ({
                                    ...prev,
                                    [candidate.itemId]: e.target.checked,
                                  }))
                                }
                                disabled={isBusy}
                              />
                              <Typography sx={styles.ocrTargetStateText}>
                                {(ocrTargets[candidate.itemId] ?? candidate.parentIsOcrTarget)
                                  ? 'ON'
                                  : 'OFF'}
                              </Typography>
                            </Box>
                            <Typography sx={styles.ocrTargetHelper}>
                              {subPatternLabel.ocrTargetHelper}
                            </Typography>
                          </Box>
                        )}
                        {/*
                          AIに見せる項目名の上書き（#2967）。
                          この様式では別の呼び方で印字されている場合に使う。
                          出力される列名は親パターンのまま変わらない
                        */}
                        <CustomTextfield
                          labelText={subPatternLabel.itemNameOverride}
                          labelBold
                          description={subPatternLabel.itemNameOverrideHelper}
                          value={itemNames[candidate.itemId] ?? ''}
                          onChange={(e): void =>
                            setItemNames(prev => ({ ...prev, [candidate.itemId]: e.target.value }))
                          }
                          // 読み取らない項目はAIに渡らないため項目名の上書きも効かない
                          disabled={isBusy || !isOcrOn(candidate)}
                          inputWidth="100%"
                          fontSizeIs14px
                          formMb="0"
                          placeholder={`未入力なら「${candidate.itemName}」のまま`}
                        />
                        <Box>
                          <CustomTextfield
                            labelText={subPatternLabel.ruleOverride}
                            labelBold
                            // 読み取らない項目に取得ルールを書いても効かないため入力させない
                            disabled={isBusy || !isOcrOn(candidate)}
                            value={rules[candidate.itemId] ?? ''}
                            onChange={(e): void =>
                              setRules(prev => ({ ...prev, [candidate.itemId]: e.target.value }))
                            }
                            multiline
                            minRows={2}
                            inputWidth="100%"
                            fontSizeIs14px
                            formMb="0"
                            placeholder="例: 右上の「発注元」欄から取得する"
                          />
                          {/*
                            通常（親パターン）の取得ルールを参考として出す。
                            上書きするときこそ「元は何だったか」を見比べたいため、
                            入力欄のすぐ下に置く
                          */}
                          {candidate.parentRule !== '' && (
                            <Typography sx={styles.parentRuleText}>
                              {subPatternLabel.parentRulePrefix}
                              {candidate.parentRule}
                            </Typography>
                          )}
                        </Box>
                        {/*
                          固定値の上書き（#3314）。
                          固定値はAIに渡すのではなく、読み取り後に空の項目へ入れる処理のため、
                          AI読取OFFの項目でも効く（むしろOFFのときは必ずこの値が入る）。
                          そのため取得ルール欄と違い !isOcrOn では無効化しない
                        */}
                        {candidate.canSetFixedValue && (
                          <Box>
                            <CustomTextfield
                              labelText={subPatternLabel.fixedValueOverride}
                              labelBold
                              description={subPatternLabel.fixedValueOverrideHelper}
                              value={fixedValues[candidate.itemId] ?? ''}
                              onChange={(e): void =>
                                setFixedValues(prev => ({
                                  ...prev,
                                  [candidate.itemId]: e.target.value,
                                }))
                              }
                              disabled={isBusy}
                              inputWidth="100%"
                              fontSizeIs14px
                              formMb="0"
                              placeholder={fixedValuePlaceholder(candidate.itemType)}
                            />
                            {candidate.parentFixedValue !== '' && (
                              <Typography sx={styles.parentRuleText}>
                                {subPatternLabel.parentFixedValuePrefix}
                                {candidate.parentFixedValue}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* チェックしていない項目でも、元の取得ルールは確認できるようにする */}
                    {!checked && candidate.parentRule !== '' && (
                      <Typography sx={styles.parentRuleText}>
                        {subPatternLabel.parentRulePrefix}
                        {candidate.parentRule}
                      </Typography>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={styles.actions}>
        <Button
          onClick={handleClose}
          disabled={isBusy}
          sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
        >
          <Typography sx={{ ...boldMd, color: gray80 }}>キャンセル</Typography>
        </Button>
        <Button
          onClick={handleSave}
          disabled={isBusy}
          sx={[primaryButtonBackgroundColor, styles.actionButton]}
        >
          <Typography sx={{ ...boldMd, color: 'white' }}>
            {submitting ? '保存中...' : '保存'}
          </Typography>
        </Button>
      </Box>

      {canUseJson && (
        <SubPatternJsonModal
          open={jsonModalOpen}
          isBulk={false}
          candidates={candidates}
          itemList={itemList}
          existingSubPatterns={existingSubPatterns}
          editingSubPatternId={variant?.subPatternId}
          initialJson={jsonModalOpen ? buildCurrentJson() : null}
          onClose={(): void => setJsonModalOpen(false)}
          onApply={(summary): void => handleJsonApply(summary.items)}
        />
      )}
    </Dialog>
  );
};

export default SubPatternEditModal;
